import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Filtres d'un écran gardés dans l'URL (cahier des charges §4) : un lien
 * partagé ou un retour arrière retrouve la même liste. Une valeur égale au
 * défaut n'apparaît pas dans l'URL ; changer un filtre revient à la page 1.
 */
export function useFiltresUrl<T extends Record<string, string>>(defauts: T) {
  const [params, setParams] = useSearchParams();

  const valeurs = useMemo(() => {
    const v = { ...defauts };
    for (const cle of Object.keys(defauts) as Array<keyof T>) {
      const lue = params.get(String(cle));
      if (lue !== null) v[cle] = lue as T[keyof T];
    }
    return v;
  }, [params]);

  const modifier = useCallback(
    (maj: Partial<T>) => {
      setParams(
        (actuels) => {
          const suivants = new URLSearchParams(actuels);
          for (const [cle, valeur] of Object.entries(maj)) {
            if (valeur === undefined || valeur === defauts[cle]) suivants.delete(cle);
            else suivants.set(cle, String(valeur));
          }
          if (!('page' in maj)) suivants.delete('page');
          return suivants;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const reinitialiser = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [setParams]);

  const actifs = Object.keys(defauts).filter((c) => c !== 'page' && c !== 'tri' && c !== 'ordre' && valeurs[c] !== defauts[c]).length;

  return { valeurs, modifier, reinitialiser, actifs };
}

/** Valeur retardée : la recherche part quand la saisie s'arrête. */
export { useDebounced } from './useDebounced';
